import { Controller } from "@hotwired/stimulus";
import * as luxon from "luxon";
import * as Turbo from "@hotwired/turbo";

export default class extends Controller {
  static targets = [
    // On the form element
    "form",

    // Master lesson inputs
    "masterLessonInstructorIdInput",
    "masterLessonVehicleIdInput",
    "masterLessonPickupLocationIdInput",
    "masterLessonDropoffLocationIdInput",
    "masterLessonPickupIsoInput",
    "masterLessonPickupDateInput",
    "masterLessonPickupTimeInput",
    "masterLessonDurationSelect",
    "masterLessonDriverCountSelect",

    // Student tabs to switch between them
    "tab1",
    "tab2",
    "tab3",
    "tab4",

    // Visual summary:
    "visualSummary",
    "student1SummaryRow",
    "student1Summary",
    "student2SummaryRow",
    "student2Summary",
    "student3SummaryRow",
    "student3Summary",
    "student4SummaryRow",
    "student4Summary",

    // Server-side package preview
    "packagePreviewPanel",
    "packagePreviewContent",

    // Each student form inputs as they appear in order
    "studentPupilIdInput1",
    "studentBtwDurationSelect1",
    "studentPickupIso1",
    "studentPickupTimeInput1",
    "studentDropoffIso1",
    "studentDropoffTimeInput1",
    "studentPickupLocationId1",
    "studentPickupLocationSelector1",
    "studentDropoffLocationId1",
    "studentDropoffLocationSelector1",

    "studentPupilIdInput2",
    "studentBtwDurationSelect2",
    "studentPickupIso2",
    "studentPickupTimeInput2",
    "studentDropoffIso2",
    "studentDropoffTimeInput2",
    "studentPickupLocationId2",
    "studentPickupLocationSelector2",
    "studentDropoffLocationId2",
    "studentDropoffLocationSelector2",

    "studentPupilIdInput3",
    "studentBtwDurationSelect3",
    "studentPickupIso3",
    "studentPickupTimeInput3",
    "studentDropoffIso3",
    "studentDropoffTimeInput3",
    "studentPickupLocationId3",
    "studentPickupLocationSelector3",
    "studentDropoffLocationId3",
    "studentDropoffLocationSelector3",

    "studentPupilIdInput4",
    "studentBtwDurationSelect4",
    "studentPickupIso4",
    "studentPickupTimeInput4",
    "studentDropoffIso4",
    "studentDropoffTimeInput4",
    "studentPickupLocationId4",
    "studentPickupLocationSelector4",
    "studentDropoffLocationId4",
    "studentDropoffLocationSelector4",

    // Bottom of form
    "errorOutput",
    "errorMessages",
    "debugOutput",

    "processedJson",
    "packagePreviewAcknowledgementsJson",
    "submitButton",
  ];

  oldStudentPickupTime = null;
  oldStudentDropoffTime = null;
  showPreviewTimeout = null;

  debugOutput = "Debug";
  errors = [];
  processedJson = "";
  observationTimeErrors = {};
  hasAttemptedSubmit = false;
  isSubmitting = false;
  packagePreviewAbortController = null;
  packagePreviewRequestTimeout = null;
  latestPackagePreviewKey = null;
  completedPackagePreviewKey = null;
  packagePreviewIsLoading = false;
  packagePreviewHasBlockingIssues = false;
  packagePreviewDebounceMs = 750;

  connect() {
    console.log("Master Lesson Admin Form Controller connected");

    setTimeout(() => {
      this.oldStudentPickupTime = this.getPickupTimeFromForm();
      this.oldStudentDropoffTime = this.getDropoffTimeFromForm();
      this.updateAllFormVisuals();
    }, 0);
  }

  disconnect() {
    if (this.packagePreviewRequestTimeout) {
      clearTimeout(this.packagePreviewRequestTimeout);
      this.packagePreviewRequestTimeout = null;
    }

    if (this.packagePreviewAbortController) {
      this.packagePreviewAbortController.abort();
      this.packagePreviewAbortController = null;
    }
  }

  /**
   * A generic input handler that can be applied to any input that triggers a change to redraw the form
   */
  onGenericChange(event) {
    // Check if this is a student selection change (for observation lessons)
    // The input has data-param-name="pupil_id" attribute
    if (event && event.target && event.target.dataset && event.target.dataset.paramName === 'pupil_id') {
      const pupilId = event.target.value;
      if (pupilId && pupilId !== "0") {
        // Validate that the student has observation time available
        this.validateStudentObservationTime(pupilId, event.target);
      } else {
        // Student was deselected, remove any observation time error
        const studentNumber = this.getStudentNumberFromInput(event.target);
        this.removeObservationTimeError(studentNumber);
      }
    }
    this.updateAllFormVisuals();
  }

  /**
   * Validates if a student has observation time available
   */
  async validateStudentObservationTime(pupilId, inputElement) {
    const drivingSchoolToken = this.data.get("drivingSchoolToken");
    if (!drivingSchoolToken) {
      console.warn("Cannot validate observation time: driving school token not found");
      return;
    }

    try {
      const response = await fetch(`/schools/${drivingSchoolToken}/check-student-observation-time`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken()
        },
        body: JSON.stringify({
          pupil_id: pupilId,
          driving_school_id: this.data.get("drivingSchoolId")
        })
      });

      const data = await response.json();

      // if (!data.has_observation_time) {
      //   // Add error to the errors array
      //   const studentNumber = this.getStudentNumberFromInput(inputElement);
      //   const errorMessage = `${data.pupil_name || `Student ${studentNumber}`} does not have observation time available. Please select a student with a package that includes observation hours, or contact the school to purchase a package with observation time.`;

      //   // Store this error so it can be displayed
      //   this.addObservationTimeError(studentNumber, errorMessage);
      // } else {
      //   // Remove any existing observation time error for this student
      //   this.removeObservationTimeError(this.getStudentNumberFromInput(inputElement));
      // }

      // Update visuals to show/hide errors
      this.updateAllFormVisuals();
    } catch (error) {
      console.error("Error validating student observation time:", error);
    }
  }

  /**
   * Get student number (1, 2, 3, or 4) from the input element
   */
  getStudentNumberFromInput(inputElement) {
    if (inputElement.id && inputElement.id.includes('parts_')) {
      const match = inputElement.id.match(/parts_(\d+)_/);
      if (match) {
        return match[1];
      }
    }
    return "1"; // Default to student 1
  }

  /**
   * Store observation time errors
   */
  addObservationTimeError(studentNumber, errorMessage) {
    this.observationTimeErrors[studentNumber] = errorMessage;
  }

  removeObservationTimeError(studentNumber) {
    delete this.observationTimeErrors[studentNumber];
  }

  packagePreviewAcknowledgements() {
    if (!this.hasPackagePreviewAcknowledgementsJsonTarget) {
      return {};
    }

    try {
      const parsed = JSON.parse(
        this.packagePreviewAcknowledgementsJsonTarget.value || "{}"
      );
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (error) {
      console.error("Error parsing package preview acknowledgements:", error);
      return {};
    }
  }

  persistPackagePreviewAcknowledgements(acknowledgements) {
    if (!this.hasPackagePreviewAcknowledgementsJsonTarget) {
      return;
    }

    this.packagePreviewAcknowledgementsJsonTarget.value = JSON.stringify(
      acknowledgements || {}
    );
  }

  onPackagePreviewAcknowledgementChange(event) {
    const pupilId = `${event.target?.dataset?.pupilId || ""}`;
    if (!pupilId) {
      return;
    }

    const acknowledgements = this.packagePreviewAcknowledgements();
    if (event.target.checked) {
      acknowledgements[pupilId] = true;
    } else {
      delete acknowledgements[pupilId];
    }

    this.persistPackagePreviewAcknowledgements(acknowledgements);
    this.updateAllFormVisuals(true);
  }

  /**
   * Used when the master pickup time is changed, as the student times might need to change too.
   */
  onMasterLessonDateOrTimeChange() {
    const newPickupTime = this.getPickupTimeFromForm(false);
    const newDropoffTime = this.getDropoffTimeFromForm();

    const oldStudent1PickupTime = this.getTimeFromTimeElement(
      this.studentPickupTimeInput1Target
    );
    if (this.oldStudentPickupTime == oldStudent1PickupTime) {
      this.studentPickupTimeInput1Target.value = newPickupTime;
      this.studentPickupTimeInput1Target.dispatchEvent(new Event("change"));
    }
    const oldStudent1DropoffTime = this.getTimeFromTimeElement(
      this.studentDropoffTimeInput1Target
    );
    if (this.oldStudentDropoffTime == oldStudent1DropoffTime) {
      this.studentDropoffTimeInput1Target.value = newDropoffTime;
      this.studentDropoffTimeInput1Target.dispatchEvent(new Event("change"));
    }

    const oldStudent2PickupTime = this.getTimeFromTimeElement(
      this.studentPickupTimeInput2Target
    );
    if (this.oldStudentPickupTime == oldStudent2PickupTime) {
      this.studentPickupTimeInput2Target.value = newPickupTime;
      this.studentPickupTimeInput2Target.dispatchEvent(new Event("change"));
    }
    const oldStudent2DropoffTime = this.getTimeFromTimeElement(
      this.studentDropoffTimeInput2Target
    );
    if (this.oldStudentDropoffTime == oldStudent2DropoffTime) {
      this.studentDropoffTimeInput2Target.value = newDropoffTime;
      this.studentDropoffTimeInput2Target.dispatchEvent(new Event("change"));
    }

    const oldStudent3PickupTime = this.getTimeFromTimeElement(
      this.studentPickupTimeInput3Target
    );
    if (this.oldStudentPickupTime == oldStudent3PickupTime) {
      this.studentPickupTimeInput3Target.value = newPickupTime;
      this.studentPickupTimeInput3Target.dispatchEvent(new Event("change"));
    }
    const oldStudent3DropoffTime = this.getTimeFromTimeElement(
      this.studentDropoffTimeInput3Target
    );
    if (this.oldStudentDropoffTime == oldStudent3DropoffTime) {
      this.studentDropoffTimeInput3Target.value = newDropoffTime;
      this.studentDropoffTimeInput3Target.dispatchEvent(new Event("change"));
    }

    const oldStudent4PickupTime = this.getTimeFromTimeElement(
      this.studentPickupTimeInput4Target
    );
    if (this.oldStudentPickupTime == oldStudent4PickupTime) {
      this.studentPickupTimeInput4Target.value = newPickupTime;
      this.studentPickupTimeInput4Target.dispatchEvent(new Event("change"));
    }
    const oldStudent4DropoffTime = this.getTimeFromTimeElement(
      this.studentDropoffTimeInput4Target
    );
    if (this.oldStudentDropoffTime == oldStudent4DropoffTime) {
      this.studentDropoffTimeInput4Target.value = newDropoffTime;
      this.studentDropoffTimeInput4Target.dispatchEvent(new Event("change"));
    }

    this.oldStudentPickupTime = newPickupTime;
    this.oldStudentDropoffTime = newDropoffTime;

    this.updateAllFormVisuals();
  }

  onFormSubmit(event) {
    this.hasAttemptedSubmit = true;
    this.updateAllFormVisuals(true);

    if(this.errors.length > 0 || this.isSubmitBlockedByPackagePreview()) {
      event.preventDefault();
      event.stopPropagation();
      this.setLoading(false);
    } else {
      this.setLoading(true);
    }
  }

  /**
   * Update the form by hiding/showing elements as needed.
   */
  lastVisualChangeId = Math.random().toString(36);
  updateAllFormVisuals(skipTimeout = false) {
    // We generate a random ID for this change and store it as the last requested change.
    // When the timeout triggers we check if it's still the last requested change before doing anything.
    // This prevents multiple rapid changes from causing multiple updates.
    // Only the last requested change will actually trigger an update.
    const currentChangeId = Math.random().toString(36);
    this.lastVisualChangeId = currentChangeId;
    console.log(`Queuing visual update ${currentChangeId}`);
    // We use a timeout to ensure that the DOM has updated before we try to read values from it.
    if (skipTimeout) {
      if (this.lastVisualChangeId === currentChangeId) {
          console.log(`Updating visuals for change ${currentChangeId}`);
          this.showAppropriateAmountOfStudentTabs();
          this.hideAndShowStudentLocationInputs();
          this.setOutputDebug();
          this.hideOrShowOutputDebug();
          this.requestPackagePreview();
          this.hidePreview();
          this.showPreview();
        } else {
          console.log(`Skipping outdated visual update ${currentChangeId}`);
        }
    } else {
      setTimeout(() => {
        if (this.lastVisualChangeId === currentChangeId) {
          console.log(`Updating visuals for change ${currentChangeId}`);
          this.showAppropriateAmountOfStudentTabs();
          this.hideAndShowStudentLocationInputs();
          this.setOutputDebug();
          this.hideOrShowOutputDebug();
          this.requestPackagePreview();
          this.hidePreview();
          this.showPreview();
        } else {
          console.log(`Skipping outdated visual update ${currentChangeId}`);
        }
      }, 100);
    }
  }

  hidePreview() {
    this.visualSummaryTarget.classList.add("u-hidden");
    this.visualSummaryTarget.classList.remove("u-table");
  }

  showPreview() {
    const processedJson = this.processedJson;
    const errors = this.errors;
    if (!processedJson || processedJson.length === 0 || errors.length > 0) {
      return;
    }

    // Remove u-hidden class
    this.visualSummaryTarget.classList.remove("u-hidden");
    this.visualSummaryTarget.classList.add("u-table");

    const numberOfDrivers = parseInt(
      this.masterLessonDriverCountSelectTarget.value,
      10
    );
    if (numberOfDrivers < 1) {
      this.student1SummaryRowTarget.classList.add("u-hidden");
      this.student1SummaryRowTarget.classList.remove("u-tableRow");
    } else {
      this.student1SummaryRowTarget.classList.remove("u-hidden");
      this.student1SummaryRowTarget.classList.add("u-tableRow");
    }

    if (numberOfDrivers < 2) {
      this.student2SummaryRowTarget.classList.add("u-hidden");
      this.student2SummaryRowTarget.classList.remove("u-tableRow");
    } else {
      this.student2SummaryRowTarget.classList.remove("u-hidden");
      this.student2SummaryRowTarget.classList.add("u-tableRow");
    }

    if (numberOfDrivers < 3) {
      this.student3SummaryRowTarget.classList.add("u-hidden");
      this.student3SummaryRowTarget.classList.remove("u-tableRow");
    } else {
      this.student3SummaryRowTarget.classList.remove("u-hidden");
      this.student3SummaryRowTarget.classList.add("u-tableRow");
    }

    if (numberOfDrivers < 4) {
      this.student4SummaryRowTarget.classList.add("u-hidden");
      this.student4SummaryRowTarget.classList.remove("u-tableRow");
    } else {
      this.student4SummaryRowTarget.classList.remove("u-hidden");
      this.student4SummaryRowTarget.classList.add("u-tableRow");
    }

    this.student1SummaryTarget.innerHTML = "";
    this.student2SummaryTarget.innerHTML = "";
    this.student3SummaryTarget.innerHTML = "";
    this.student4SummaryTarget.innerHTML = "";
    // Calculate the width of student1SummaryTarget
    setTimeout(() => {
      const rect1 = this.student1SummaryTarget.getBoundingClientRect();
      const rect2 = this.student2SummaryTarget.getBoundingClientRect();
      const rect3 = this.student3SummaryTarget.getBoundingClientRect();
      const rect4 = this.student4SummaryTarget.getBoundingClientRect();
      const width = rect1.width;

      // Height is 10px, so need top to be half of the height minus 10px
      const top1 = rect1.height / 2 - 5;
      const top2 = rect2.height / 2 - 5;
      const top3 = rect3.height / 2 - 5;
      const top4 = rect4.height / 2 - 5;

      // For each bit in the processedJson, create a span with a width proportional to the duration
      const assignments = JSON.parse(processedJson);
      const totalDuration = assignments.reduce((sum, assignment) => {
        return sum + (assignment.duration || 0);
      }, 0);

      let left = 0;
      assignments.forEach((assignment) => {
        const isStudent1Driving = assignment.driver === "1";
        const isStudent1Observing = assignment.observers.includes("1");
        const isStudent2Driving = assignment.driver === "2";
        const isStudent2Observing = assignment.observers.includes("2");
        const isStudent3Driving = assignment.driver === "3";
        const isStudent3Observing = assignment.observers.includes("3");
        const isStudent4Driving = assignment.driver === "4";
        const isStudent4Observing = assignment.observers.includes("4");

        const widthInPixels =
          (assignment.duration / totalDuration) * (width - 0); // 0 for padding
        const span1 = document.createElement("span");
        span1.style.display = "block";
        span1.style.width = `${widthInPixels}px`;
        span1.style.height = "10px";
        span1.style.position = "absolute";
        span1.style.top = `${top1}px`;
        span1.style.left = `${left}px`;
        span1.style.backgroundColor = `${
          isStudent1Driving
            ? "lightblue"
            : isStudent1Observing
            ? "peachpuff"
            : "transparent"
        }`;
        this.student1SummaryTarget.appendChild(span1);

        const span2 = document.createElement("span");
        span2.style.display = "block";
        span2.style.width = `${widthInPixels}px`;
        span2.style.height = "10px";
        span2.style.position = "absolute";
        span2.style.top = `${top2}px`;
        span2.style.left = `${left}px`;
        span2.style.backgroundColor = `${
          isStudent2Driving
            ? "lightblue"
            : isStudent2Observing
            ? "peachpuff"
            : "transparent"
        }`;
        this.student2SummaryTarget.appendChild(span2);

        const span3 = document.createElement("span");
        span3.style.display = "block";
        span3.style.width = `${widthInPixels}px`;
        span3.style.height = "10px";
        span3.style.position = "absolute";
        span3.style.top = `${top3}px`;
        span3.style.left = `${left}px`;
        span3.style.backgroundColor = `${
          isStudent3Driving
            ? "lightblue"
            : isStudent3Observing
            ? "peachpuff"
            : "transparent"
        }`;
        this.student3SummaryTarget.appendChild(span3);

        const span4 = document.createElement("span");
        span4.style.display = "block";
        span4.style.width = `${widthInPixels}px`;
        span4.style.height = "10px";
        span4.style.position = "absolute";
        span4.style.top = `${top4}px`;
        span4.style.left = `${left}px`;
        span4.style.backgroundColor = `${
          isStudent4Driving
            ? "lightblue"
            : isStudent4Observing
            ? "peachpuff"
            : "transparent"
        }`;
        this.student4SummaryTarget.appendChild(span4);

        left += widthInPixels;
      });
    });

    // <span
    //         style="display: block; width: 25px; background-color: lightgray; height: 20px; position: absolute; top: 0; left: 0;"
    //       ></span>
  }

  requestPackagePreview() {
    if (!this.hasPackagePreviewPanelTarget || !this.hasPackagePreviewContentTarget) {
      this.packagePreviewIsLoading = false;
      this.packagePreviewHasBlockingIssues = false;
      return;
    }

    if (!this.shouldFetchPackagePreview()) {
      this.clearPackagePreview();
      return;
    }

    const previewKey = JSON.stringify(this.buildPackagePreviewState());

    if (this.packagePreviewIsLoading && this.latestPackagePreviewKey === previewKey) {
      return;
    }

    if (!this.packagePreviewIsLoading && this.completedPackagePreviewKey === previewKey) {
      return;
    }

    this.latestPackagePreviewKey = previewKey;

    if (this.packagePreviewRequestTimeout) {
      clearTimeout(this.packagePreviewRequestTimeout);
    }

    if (this.packagePreviewAbortController) {
      this.packagePreviewAbortController.abort();
      this.packagePreviewAbortController = null;
    }

    this.showPackagePreviewLoading();

    this.packagePreviewRequestTimeout = setTimeout(async () => {
      this.packagePreviewRequestTimeout = null;
      const formData = new FormData(this.formTarget);
      const masterLessonId = this.data.get("masterLessonId");
      if (masterLessonId) {
        formData.set("master_lesson_id", masterLessonId);
      }

      const abortController = new AbortController();
      this.packagePreviewAbortController = abortController;

      try {
        const response = await fetch(this.data.get("packagePreviewUrl"), {
          method: "POST",
          headers: {
            Accept: "application/json",
            "X-CSRF-Token": this.csrfToken(),
          },
          body: formData,
          signal: abortController.signal,
        });

        const data = await response.json();
        if (this.latestPackagePreviewKey !== previewKey) {
          return;
        }

        this.completedPackagePreviewKey = previewKey;
        this.renderPackagePreview(data.package_preview, data.errors || []);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }

        if (this.latestPackagePreviewKey !== previewKey) {
          return;
        }

        this.completedPackagePreviewKey = previewKey;
        this.packagePreviewIsLoading = false;
        this.packagePreviewHasBlockingIssues = false;
        this.refreshSubmitButtonState();
        this.packagePreviewPanelTarget.classList.remove("u-hidden");
        this.packagePreviewContentTarget.innerHTML = `
          <div class="Panel  Panel--alert  Panel--borderless  p2">
            <strong class="u-block">Package preview unavailable</strong>
            <p class="mt1  mb0">We couldn't load projected package usage just now.</p>
          </div>
        `;
      } finally {
        if (this.packagePreviewAbortController === abortController) {
          this.packagePreviewAbortController = null;
        }
      }
    }, this.packagePreviewDebounceMs);
  }

  shouldFetchPackagePreview() {
    const previewUrl = this.data.get("packagePreviewUrl");
    return Boolean(
      previewUrl &&
        this.processedJson &&
        this.processedJson.length > 0 &&
        this.errors.length === 0 &&
        this.hasAllRequiredPupilSelections()
    );
  }

  buildPackagePreviewState() {
    return {
      masterLessonId: this.data.get("masterLessonId") || "",
      vehicleId: this.masterLessonVehicleIdInputTarget.value,
      pupilIds: [
        this.studentPupilIdInput1Target.value,
        this.studentPupilIdInput2Target.value,
        this.studentPupilIdInput3Target.value,
        this.studentPupilIdInput4Target.value,
      ],
      acknowledgements: this.packagePreviewAcknowledgements(),
      assignments: this.buildPackagePreviewAssignments(),
    };
  }

  buildPackagePreviewAssignments() {
    if (!this.processedJson || this.processedJson.length === 0) {
      return [];
    }

    try {
      const assignments = JSON.parse(this.processedJson);
      if (!Array.isArray(assignments)) {
        return [];
      }

      return assignments.map((assignment) => ({
        duration: parseInt(assignment?.duration, 10) || 0,
        driver: assignment?.driver || "",
        observers: Array.isArray(assignment?.observers)
          ? assignment.observers.map((observer) => `${observer}`).sort()
          : [],
      }));
    } catch (error) {
      console.error("Error building package preview assignments:", error);
      return [{ processedJson: this.processedJson }];
    }
  }

  hasAllRequiredPupilSelections() {
    const numberOfDrivers = parseInt(
      this.masterLessonDriverCountSelectTarget.value,
      10
    );

    if (
      numberOfDrivers >= 1 &&
      (!this.studentPupilIdInput1Target.value ||
        this.studentPupilIdInput1Target.value === "0")
    ) {
      return false;
    }

    if (
      numberOfDrivers >= 2 &&
      (!this.studentPupilIdInput2Target.value ||
        this.studentPupilIdInput2Target.value === "0")
    ) {
      return false;
    }

    if (
      numberOfDrivers >= 3 &&
      (!this.studentPupilIdInput3Target.value ||
        this.studentPupilIdInput3Target.value === "0")
    ) {
      return false;
    }

    if (
      numberOfDrivers >= 4 &&
      (!this.studentPupilIdInput4Target.value ||
        this.studentPupilIdInput4Target.value === "0")
    ) {
      return false;
    }

    return true;
  }

  showPackagePreviewLoading() {
    if (!this.hasPackagePreviewPanelTarget || !this.hasPackagePreviewContentTarget) {
      return;
    }

    this.packagePreviewIsLoading = true;
    this.packagePreviewHasBlockingIssues = false;
    this.refreshSubmitButtonState();
    this.packagePreviewPanelTarget.classList.remove("u-hidden");
    this.packagePreviewContentTarget.innerHTML = `
      <p class="u-textMuted  my0">Checking current package balances...</p>
    `;
  }

  clearPackagePreview() {
    if (this.packagePreviewRequestTimeout) {
      clearTimeout(this.packagePreviewRequestTimeout);
      this.packagePreviewRequestTimeout = null;
    }

    if (this.packagePreviewAbortController) {
      this.packagePreviewAbortController.abort();
      this.packagePreviewAbortController = null;
    }

    this.latestPackagePreviewKey = null;
    this.completedPackagePreviewKey = null;
    this.packagePreviewIsLoading = false;
    this.packagePreviewHasBlockingIssues = false;
    this.refreshSubmitButtonState();
    if (!this.hasPackagePreviewPanelTarget || !this.hasPackagePreviewContentTarget) {
      return;
    }

    this.packagePreviewPanelTarget.classList.add("u-hidden");
    this.packagePreviewContentTarget.innerHTML = "";
  }

  renderPackagePreview(packagePreview, errors = []) {
    if (!this.hasPackagePreviewPanelTarget || !this.hasPackagePreviewContentTarget) {
      this.packagePreviewIsLoading = false;
      this.packagePreviewHasBlockingIssues = false;
      this.refreshSubmitButtonState();
      return;
    }

    const attempts = packagePreview?.pupil_assignment_attempts || [];
    if (attempts.length === 0) {
      this.clearPackagePreview();
      return;
    }

    const observationSetting = packagePreview.observation_setting || {};
    const usesSelectedStudentsCount =
      packagePreview.using_selected_students_count_for_package_time === true;
    const summaryBits = [
      `${formatMinutesLabel(packagePreview.total_duration_in_minutes)} total`,
      observationSetting.structure_for_observations || "turns",
      usesSelectedStudentsCount
        ? `${packagePreview.selected_students_count || 1} students selected`
        : `${observationSetting.maximum_students_per_lesson || 1} students max`,
    ];

    const blockingAttempts = attempts.filter((attempt) => Boolean(attempt.blocks_save));
    const overrideAttempts = attempts.filter((attempt) => attempt.preview_status === "btw_override");
    const acknowledgedAttempts = attempts.filter((attempt) =>
      ["admin_acknowledged_partial", "admin_acknowledged_no_package"].includes(
        attempt.preview_status
      )
    );
    this.packagePreviewIsLoading = false;
    this.packagePreviewHasBlockingIssues =
      errors.length > 0 || blockingAttempts.length > 0;
    this.refreshSubmitButtonState();
    const attemptLookupStrings = blockingAttempts.map((attempt) =>
      previewStudentLabel(attempt)
    );
    const generalErrors = errors.filter((error) => {
      return !attemptLookupStrings.some((lookup) => `${error}`.includes(lookup));
    });

    let statusHtml = `
      <p class="mt0  mb15" style="color:#2f7d32;"><strong>All selected students have a matching package.</strong></p>
    `;

    if (blockingAttempts.length > 0) {
      statusHtml = `
        <p class="mt0  mb15" style="color:#b04545;">
          <strong>${blockingAttempts.length} issue${blockingAttempts.length === 1 ? "" : "s"}</strong>
          <span class="u-textMuted"> - ${escapeHtml(
            blockingAttempts.map((attempt) => previewStudentLabel(attempt)).join(", ")
          )}</span>
        </p>
      `;
    } else if (acknowledgedAttempts.length > 0 || overrideAttempts.length > 0) {
      const summaryLabels = [];
      if (acknowledgedAttempts.length > 0) {
        summaryLabels.push(
          `${acknowledgedAttempts.length} admin acknowledgement${
            acknowledgedAttempts.length === 1 ? "" : "s"
          }`
        );
      }
      if (overrideAttempts.length > 0) {
        summaryLabels.push(
          `${overrideAttempts.length} BTW override${
            overrideAttempts.length === 1 ? "" : "s"
          }`
        );
      }

      statusHtml = `
        <p class="mt0  mb15" style="color:#9a6b00;">
          <strong>${escapeHtml(summaryLabels.join(" / "))}</strong>
          <span class="u-textMuted"> - ${escapeHtml(
            [...acknowledgedAttempts, ...overrideAttempts]
              .map((attempt) => previewStudentLabel(attempt))
              .join(", ")
          )}</span>
        </p>
      `;
    }

    const errorHtml =
      generalErrors.length > 0
        ? `
          <div class="Panel  Panel--alert  Panel--borderless  p2  mb2">
            ${generalErrors
              .map(
                (error) =>
                  `<p class="mt0  mb0">${escapeHtml(sanitizePreviewText(error))}</p>`
              )
              .join("")}
          </div>
        `
        : "";

    this.packagePreviewPanelTarget.classList.remove("u-hidden");
    this.packagePreviewContentTarget.innerHTML = `
      <p class="u-textMuted  mt0  mb15">${summaryBits
        .map((bit) => escapeHtml(bit))
        .join(" / ")}</p>
      ${statusHtml}
      ${errorHtml}
      ${attempts
        .map((attempt) => this.renderPackagePreviewAttempt(attempt))
        .join("")}
    `;
  }

  renderPackagePreviewAttempt(attempt) {
    const pupilLabel = previewStudentLabel(attempt);
    const previewStatus = attempt.preview_status || "matched";
    const statusMeta = packagePreviewStatusMeta(previewStatus);
    const hasSelectedPackage = Boolean(attempt.selected_purchase_history_id);
    const isMultiPackageSplit = previewStatus === "multi_package_split";
    const isBtwOverride = previewStatus === "btw_override";
    const isAdminAcknowledged = [
      "admin_acknowledged_partial",
      "admin_acknowledged_no_package",
    ].includes(previewStatus);
    const requiresAdminAcknowledgement = Boolean(
      attempt.requires_admin_package_time_acknowledgement
    );
    const packageLabel = hasSelectedPackage
      ? previewPackageLabel(attempt.selected_package_name)
      : "No package time available";
    const selectedPackageAllocations = Array.isArray(attempt.selected_package_allocations)
      ? attempt.selected_package_allocations
      : [];
    const summaryLine =
      previewStatus === "matched"
        ? `${pupilLabel} / ${packageLabel} / after ${formatMinutesLabel(
          attempt.selected_projected_remaining_drive_time
        )} drive / ${formatMinutesLabel(
          attempt.selected_projected_remaining_observation_time
        )} observation`
        : isMultiPackageSplit
        ? `${pupilLabel} / uses ${selectedPackageAllocations.length} packages / ${formatMinutesLabel(
            attempt.will_use_drive_time
          )} drive / ${formatMinutesLabel(
            attempt.will_use_observation_time
          )} observation`
        : isBtwOverride
        ? `${pupilLabel} / ${packageLabel} / BTW covers some observation time`
        : isAdminAcknowledged
        ? hasSelectedPackage
          ? `${pupilLabel} / ${packageLabel} / admin acknowledgement uses ${formatMinutesLabel(
              attempt.will_use_drive_time
            )} drive / ${formatMinutesLabel(
              attempt.will_use_observation_time
            )} observation`
          : `${pupilLabel} / admin acknowledgement / no package time available`
        : `${pupilLabel} / ${buildPackagePreviewShortfallLabel(attempt)}`;

    const selectedPackageLine = hasSelectedPackage
      ? `
        <p class="mt1  mb0"><strong>Package:</strong> ${escapeHtml(packageLabel)}</p>
        <p class="mt1  mb0"><strong>After save:</strong> ${formatMinutesLabel(
          attempt.selected_projected_remaining_drive_time
        )} drive / ${formatMinutesLabel(
          attempt.selected_projected_remaining_observation_time
        )} observation</p>
      `
      : "";
    const selectedPackageAllocationsLine =
      isMultiPackageSplit && selectedPackageAllocations.length > 0
        ? `
          <div class="mt2">
            <p class="mt1  mb1">The following packages will be used:</p>
            ${selectedPackageAllocations
              .map((allocation) => this.renderSelectedPackageAllocation(allocation))
              .join("")}
          </div>
        `
        : "";

    const candidatePackages = Array.isArray(attempt.candidate_packages)
      ? attempt.candidate_packages
      : [];

    const candidateDetails =
      previewStatus !== "matched" && candidatePackages.length > 0
        ? `
          <div class="mt1">
            <p class="u-textMuted  mt0  mb1"><strong>Options:</strong> ${candidatePackages.length}</p>
            ${candidatePackages
              .map((candidate) => this.renderCandidatePackage(candidate))
              .join("")}
          </div>
        `
        : "";

    const usageDetails = isAdminAcknowledged
      ? `
        <p class="mt1  mb0"><strong>Standard use:</strong> ${formatMinutesLabel(
          attempt.calculated_drive_time
        )} drive / ${formatMinutesLabel(
          attempt.calculated_observation_time
        )} observation</p>
        <p class="mt1  mb0"><strong>This lesson will use:</strong> ${formatMinutesLabel(
          attempt.will_use_drive_time
        )} drive / ${formatMinutesLabel(
          attempt.will_use_observation_time
        )} observation</p>
        <p class="mt1  mb0"><strong>Not covered by package:</strong> ${formatMinutesLabel(
          attempt.uncovered_package_time
        )}</p>
      `
      : isBtwOverride
      ? `
        <p class="mt1  mb0"><strong>Standard use:</strong> ${formatMinutesLabel(
          attempt.calculated_drive_time
        )} drive / ${formatMinutesLabel(
          attempt.calculated_observation_time
        )} observation</p>
        <p class="mt1  mb0"><strong>This lesson will use:</strong> ${formatMinutesLabel(
          attempt.will_use_drive_time
        )} drive / ${formatMinutesLabel(
          attempt.will_use_observation_time
        )} observation</p>
        <p class="mt1  mb0"><strong>Observation short:</strong> ${formatMinutesLabel(
          attempt.observation_time_shortfall
        )}</p>
      `
      : requiresAdminAcknowledgement
      ? `
        <p class="mt1  mb0"><strong>Standard use:</strong> ${formatMinutesLabel(
          attempt.calculated_drive_time
        )} drive / ${formatMinutesLabel(
          attempt.calculated_observation_time
        )} observation</p>
        <p class="mt1  mb0"><strong>With admin acknowledgement:</strong> ${formatMinutesLabel(
          attempt.acknowledged_drive_time_to_use
        )} drive / ${formatMinutesLabel(
          attempt.acknowledged_observation_time_to_use
        )} observation</p>
        <p class="mt1  mb0"><strong>Not covered by package:</strong> ${formatMinutesLabel(
          attempt.acknowledged_uncovered_package_time
        )}</p>
      `
      : `
        <p class="mt1  mb0"><strong>This lesson will use:</strong> ${formatMinutesLabel(
          attempt.will_use_drive_time
        )} drive / ${formatMinutesLabel(
          attempt.will_use_observation_time
        )} observation</p>
      `;

    const acknowledgementControl = requiresAdminAcknowledgement
      ? `
        <label
          class="u-inlineFlex  u-alignItemsCenter  mt1"
          style="gap:8px;"
        >
          <div class="Input--checkbox  Input--checkbox--light  mb1">
            <input
              type="checkbox"
              name="package_preview_acknowledgement_${escapeHtml(attempt.pupil_id)}"
              id="package_preview_acknowledgement_${escapeHtml(attempt.pupil_id)}"
              data-pupil-id="${escapeHtml(attempt.pupil_id)}"
              data-action="change->master-lesson-admin-form#onPackagePreviewAcknowledgementChange"
              ${attempt.admin_package_time_acknowledged ? "checked" : ""}
              class="Input--checkbox-input"
            >
            <label class="Input--checkbox-label" for="package_preview_acknowledgement_${escapeHtml(attempt.pupil_id)}">
              Override missing time and schedule anyway
            </label>
          </div>
        </label>
      `
      : "";

    const detailsOpenAttr =
      previewStatus === "blocked" || isAdminAcknowledged ? " open" : "";

    return `
      <details class="pt1  pb1" style="border-top:1px solid #ececec;"${detailsOpenAttr}>
        <summary
          style="
            cursor:pointer;
            list-style:none;
            display:flex;
            justify-content:space-between;
            align-items:center;
            gap:12px;
            padding:10px 12px;
            border:1px solid #ececec;
            border-left:4px solid ${statusMeta.accentColor};
            border-radius:6px;
            background:${statusMeta.backgroundColor};
          "
        >
          <span style="color:${statusMeta.accentColor};">${escapeHtml(summaryLine)}</span>

          <span class="Button  Button--small  Button--outline  Button--slim  Button--noWrap  px1">
            <span class="u-textTiny  u-textUppercase">Details</span> <span class="icon  icon-ctrl  u-inlineBlock  u-rotate180  u-alignTop"></span>
          </span>
        </summary>
        <div class="mt1  ml1">
          <p class="mt1  mb2"><strong>Current balance:</strong> ${formatMinutesLabel(
            attempt.current_total_remaining_drive_time
          )} drive / ${formatMinutesLabel(
            attempt.current_total_remaining_observation_time
          )} observation</p>
          ${acknowledgementControl}
          ${usageDetails}
          ${isMultiPackageSplit ? selectedPackageAllocationsLine : selectedPackageLine}
          ${candidateDetails}
        </div>
      </details>
    `;
  }

  renderSelectedPackageAllocation(allocation) {
    return `
      <div class="mb2  pb2" style="border-bottom:1px solid #ececec;">
        <p class="mt0  mb0">
          <strong>Package:</strong> ${escapeHtml(previewPackageLabel(allocation.package_name))}
        </p>
        <p class="mt1  mb0">
          <strong>Remaining:</strong>
          ${formatMinutesLabel(allocation.remaining_drive_time)} drive /
          ${formatMinutesLabel(allocation.remaining_observation_time)} observation
        </p>
        <p class="mt1  mb0">
          <strong>After save:</strong>
          ${formatMinutesLabel(allocation.projected_remaining_drive_time)} drive /
          ${formatMinutesLabel(allocation.projected_remaining_observation_time)} observation
        </p>
      </div>
    `;
  }

  renderCandidatePackage(candidate) {
    const flags = [];

    if (candidate.selected) {
      flags.push("selected");
    }
    if (!candidate.transmission_match) {
      flags.push("wrong transmission");
    }
    if (!candidate.drive_time_match) {
      flags.push("drive short");
    }
    if (!candidate.observation_time_match) {
      flags.push("observation short");
    }
    if (flags.length === 0) {
      flags.push("matches");
    }

    return `
      <div class="mb1">
        <p class="mt0  mb0">
          <strong>${escapeHtml(previewPackageLabel(candidate.package_name))}</strong>
        </p>
        <p class="u-textMuted  mt1  mb0">
          ${formatMinutesLabel(candidate.remaining_drive_time)} drive /
          ${formatMinutesLabel(candidate.remaining_observation_time)} observation remaining /
          ${escapeHtml(flags.join(", "))}
        </p>
      </div>
    `;
  }

  setOutputDebug() {
    this.errors = [];
    this.debugOutput = "";
    this.processedJson = "";

    // Add observation time errors if any
    Object.keys(this.observationTimeErrors).forEach(studentNumber => {
      this.errors.push(this.observationTimeErrors[studentNumber]);
    });

    const numberOfDrivers = parseInt(
      this.masterLessonDriverCountSelectTarget.value,
      10
    );

    const masterPickupIso = this.getPickupTimeIsoFromForm();
    if (!masterPickupIso) {
      this.errors.push("Pickup time is not set");
      return;
    }
    const masterDropoffIso = this.getDropoffIsoFromForm();
    if (!masterDropoffIso) {
      this.errors.push("Dropoff time is not set");
      return;
    }

    if (!this.isIsoValid(masterPickupIso)) {
      this.errors.push(
        "Pickup time is not valid, it should be to the nearest 5 minutes"
      );
      return;
    }
    if (!this.isIsoValid(masterDropoffIso)) {
      this.errors.push(
        "Dropoff time is not valid, it should be to the nearest 5 minutes"
      );
      return;
    }

    const instructorId = this.masterLessonInstructorIdInputTarget.value;
    if (!instructorId) {
      this.errors.push("Instructor is not set");
      return;
    }

    const vehicleId = this.masterLessonVehicleIdInputTarget.value;
    if (!vehicleId) {
      this.errors.push("Vehicle is not set");
      return;
    }

    let splitDrivers = [];

    const startTimeInteger = get5MinuteIntegerFromIsoTime(
      masterPickupIso,
      masterPickupIso,
      this.data.get("drivingSchoolTimezone")
    );
    const endTimeInteger = get5MinuteIntegerFromIsoTime(
      masterPickupIso,
      masterDropoffIso,
      this.data.get("drivingSchoolTimezone")
    );

    const student1PickupIso = this.studentPickupIso1Target.value;
    const student1DropoffIso = this.studentDropoffIso1Target.value;
    const student1DrivingMinutes = parseInt(
      this.studentBtwDurationSelect1Target.value,
      10
    );
    if (numberOfDrivers >= 1) {
      const pupilId = this.studentPupilIdInput1Target.value;
      if ((!pupilId || pupilId === "0") && this.hasAttemptedSubmit) {
        this.errors.push("Student 1 is not set");
        return;
      }

      if (student1PickupIso && student1DropoffIso) {
        // Isos must be multiple of 5 minutes
        if (!this.isIsoValid(student1PickupIso)) {
          this.errors.push(
            "Student 1 pickup time is not valid, must be to the nearest 5 minutes"
          );
          return;
        }
        if (!this.isIsoValid(student1DropoffIso)) {
          this.errors.push(
            "Student 1 dropoff time is not valid, must be to the nearest 5 minutes"
          );
          return;
        }

        // Start and end must be between master pickup and dropoff
        const isAfterMasterPickup =
          luxon.DateTime.fromISO(student1PickupIso, {
            zone: this.data.get("drivingSchoolTimezone"),
          }) >=
          luxon.DateTime.fromISO(masterPickupIso, {
            zone: this.data.get("drivingSchoolTimezone"),
          });
        const isBeforeMasterDropoff =
          luxon.DateTime.fromISO(student1DropoffIso, {
            zone: this.data.get("drivingSchoolTimezone"),
          }) <=
          luxon.DateTime.fromISO(masterDropoffIso, {
            zone: this.data.get("drivingSchoolTimezone"),
          });
        if (!isAfterMasterPickup || !isBeforeMasterDropoff) {
          this.errors.push(
            "Student 1 pickup/dropoff times must be between master pickup and dropoff times"
          );
          return;
        }

        const startInt = get5MinuteIntegerFromIsoTime(
          masterPickupIso,
          student1PickupIso,
          this.data.get("drivingSchoolTimezone")
        );
        const endInt = get5MinuteIntegerFromIsoTime(
          masterPickupIso,
          student1DropoffIso,
          this.data.get("drivingSchoolTimezone")
        );

        // End int must be after start int
        if (endInt <= startInt) {
          this.errors.push("Student 1 dropoff time must be after pickup time");
          return;
        }

        splitDrivers.push({
          name: "1",
          start: startInt,
          end: endInt,
          drivingTime: Math.ceil(student1DrivingMinutes / 5),
        });
      } else {
        // If student 1 is a driver they must have pickup and dropoff times
        this.errors.push("Student 1 pickup/dropoff times are not set");
        return;
      }
    }

    const student2PickupIso = this.studentPickupIso2Target.value;
    const student2DropoffIso = this.studentDropoffIso2Target.value;
    const student2DrivingMinutes = parseInt(
      this.studentBtwDurationSelect2Target.value,
      10
    );
    if (numberOfDrivers >= 2) {
      const pupilId = this.studentPupilIdInput2Target.value;
      if ((!pupilId || pupilId === "0") && this.hasAttemptedSubmit) {
        this.errors.push("Student 2 is not set");
        return;
      }

      if (student2PickupIso && student2DropoffIso) {
        // Isos must be multiple of 5 minutes
        if (!this.isIsoValid(student2PickupIso)) {
          this.errors.push(
            "Student 2 pickup time is not valid, must be to the nearest 5 minutes"
          );
          return;
        }
        if (!this.isIsoValid(student2DropoffIso)) {
          this.errors.push(
            "Student 2 dropoff time is not valid, must be to the nearest 5 minutes"
          );
          return;
        }

        // Start and end must be between master pickup and dropoff
        const isAfterMasterPickup =
          luxon.DateTime.fromISO(student2PickupIso, {
            zone: this.data.get("drivingSchoolTimezone"),
          }) >=
          luxon.DateTime.fromISO(masterPickupIso, {
            zone: this.data.get("drivingSchoolTimezone"),
          });
        const isBeforeMasterDropoff =
          luxon.DateTime.fromISO(student2DropoffIso, {
            zone: this.data.get("drivingSchoolTimezone"),
          }) <=
          luxon.DateTime.fromISO(masterDropoffIso, {
            zone: this.data.get("drivingSchoolTimezone"),
          });
        if (!isAfterMasterPickup || !isBeforeMasterDropoff) {
          this.errors.push(
            "Student 2 pickup/dropoff times must be between master pickup and dropoff times"
          );
          return;
        }

        const startInt = get5MinuteIntegerFromIsoTime(
          masterPickupIso,
          student2PickupIso,
          this.data.get("drivingSchoolTimezone")
        );
        const endInt = get5MinuteIntegerFromIsoTime(
          masterPickupIso,
          student2DropoffIso,
          this.data.get("drivingSchoolTimezone")
        );

        // End int must be after start int
        if (endInt <= startInt) {
          this.errors.push("Student 2 dropoff time must be after pickup time");
          return;
        }

        splitDrivers.push({
          name: "2",
          start: startInt,
          end: endInt,
          drivingTime: Math.ceil(student2DrivingMinutes / 5),
        });
      } else {
        // If student 2 is a driver they must have pickup and dropoff times
        this.errors.push("Student 2 pickup/dropoff times are not set");
        return;
      }
    }

    const student3PickupIso = this.studentPickupIso3Target.value;
    const student3DropoffIso = this.studentDropoffIso3Target.value;
    const student3DrivingMinutes = parseInt(
      this.studentBtwDurationSelect3Target.value,
      10
    );
    if (numberOfDrivers >= 3) {
      const pupilId = this.studentPupilIdInput3Target.value;
      if ((!pupilId || pupilId === "0") && this.hasAttemptedSubmit) {
        this.errors.push("Student 3 is not set");
        return;
      } else {
        console.log(`Student 3 is set to ${pupilId}`);
      }

      if (student3PickupIso && student3DropoffIso) {
        // Isos must be multiple of 5 minutes
        if (!this.isIsoValid(student3PickupIso)) {
          this.errors.push(
            "Student 3 pickup time is not valid, must be to the nearest 5 minutes"
          );
          return;
        }
        if (!this.isIsoValid(student3DropoffIso)) {
          this.errors.push(
            "Student 3 dropoff time is not valid, must be to the nearest 5 minutes"
          );
          return;
        }

        // Start and end must be between master pickup and dropoff
        const isAfterMasterPickup =
          luxon.DateTime.fromISO(student3PickupIso, {
            zone: this.data.get("drivingSchoolTimezone"),
          }) >=
          luxon.DateTime.fromISO(masterPickupIso, {
            zone: this.data.get("drivingSchoolTimezone"),
          });
        const isBeforeMasterDropoff =
          luxon.DateTime.fromISO(student3DropoffIso, {
            zone: this.data.get("drivingSchoolTimezone"),
          }) <=
          luxon.DateTime.fromISO(masterDropoffIso, {
            zone: this.data.get("drivingSchoolTimezone"),
          });
        if (!isAfterMasterPickup || !isBeforeMasterDropoff) {
          this.errors.push(
            "Student 3 pickup/dropoff times must be between master pickup and dropoff times"
          );
          return;
        }

        const startInt = get5MinuteIntegerFromIsoTime(
          masterPickupIso,
          student3PickupIso,
          this.data.get("drivingSchoolTimezone")
        );
        const endInt = get5MinuteIntegerFromIsoTime(
          masterPickupIso,
          student3DropoffIso,
          this.data.get("drivingSchoolTimezone")
        );

        // End int must be after start int
        if (endInt <= startInt) {
          this.errors.push("Student 3 dropoff time must be after pickup time");
          return;
        }

        splitDrivers.push({
          name: "3",
          start: startInt,
          end: endInt,
          drivingTime: Math.ceil(student3DrivingMinutes / 5),
        });
      } else {
        // If student 3 is a driver they must have pickup and dropoff times
        this.errors.push("Student 3 pickup/dropoff times are not set");
        return;
      }
    }

    const student4PickupIso = this.studentPickupIso4Target.value;
    const student4DropoffIso = this.studentDropoffIso4Target.value;
    const student4DrivingMinutes = parseInt(
      this.studentBtwDurationSelect4Target.value,
      10
    );
    if (numberOfDrivers >= 4) {
            const pupilId = this.studentPupilIdInput4Target.value;
      if ((!pupilId || pupilId === "0") && this.hasAttemptedSubmit) {
        this.errors.push("Student 4 is not set");
        return;
      } else {
        console.log(`Student 4 is set to ${pupilId}`);
      }

      if (student4PickupIso && student4DropoffIso) {
        // Isos must be multiple of 5 minutes
        if (!this.isIsoValid(student4PickupIso)) {
          this.errors.push(
            "Student 4 pickup time is not valid, must be to the nearest 5 minutes"
          );
          return;
        }
        if (!this.isIsoValid(student4DropoffIso)) {
          this.errors.push(
            "Student 4 dropoff time is not valid, must be to the nearest 5 minutes"
          );
          return;
        }

        // Start and end must be between master pickup and dropoff
        const isAfterMasterPickup =
          luxon.DateTime.fromISO(student4PickupIso, {
            zone: this.data.get("drivingSchoolTimezone"),
          }) >=
          luxon.DateTime.fromISO(masterPickupIso, {
            zone: this.data.get("drivingSchoolTimezone"),
          });
        const isBeforeMasterDropoff =
          luxon.DateTime.fromISO(student4DropoffIso, {
            zone: this.data.get("drivingSchoolTimezone"),
          }) <=
          luxon.DateTime.fromISO(masterDropoffIso, {
            zone: this.data.get("drivingSchoolTimezone"),
          });
        if (!isAfterMasterPickup || !isBeforeMasterDropoff) {
          this.errors.push(
            "Student 4 pickup/dropoff times must be between master pickup and dropoff times"
          );
          return;
        }

        const startInt = get5MinuteIntegerFromIsoTime(
          masterPickupIso,
          student4PickupIso,
          this.data.get("drivingSchoolTimezone")
        );
        const endInt = get5MinuteIntegerFromIsoTime(
          masterPickupIso,
          student4DropoffIso,
          this.data.get("drivingSchoolTimezone")
        );

        // End int must be after start int
        if (endInt <= startInt) {
          this.errors.push("Student 4 dropoff time must be after pickup time");
          return;
        }

        splitDrivers.push({
          name: "4",
          start: startInt,
          end: endInt,
          drivingTime: Math.ceil(student4DrivingMinutes / 5),
        });
      } else {
        // If student 4 is a driver they must have pickup and dropoff times
        this.errors.push("Student 4 pickup/dropoff times are not set");
        return;
      }
    }

    let splitResult = splitLessonTimeAmongDrivers(endTimeInteger, splitDrivers);

    if (!splitResult.success) {
      this.errors.push(...splitResult.errors);
    } else {
      let convertedAssignments = splitResult.assignment.map((assignment) => {
        return {
          start: convert5MinuteIntegerToIsoTime(
            masterPickupIso,
            assignment.start,
            this.data.get("drivingSchoolTimezone")
          ),
          end: convert5MinuteIntegerToIsoTime(
            masterPickupIso,
            assignment.end,
            this.data.get("drivingSchoolTimezone")
          ),
          duration: assignment.duration * 5,
          driver: assignment.driver,
        };
      });

      let uniqueIsos = [];
      convertedAssignments.forEach((assignment) => {
        uniqueIsos.push(assignment.start);
        uniqueIsos.push(assignment.end);
      });
      // Add all driver pickup/driopoff times too
      if (student1PickupIso && numberOfDrivers >= 1)
        uniqueIsos.push(student1PickupIso);
      if (student1DropoffIso && numberOfDrivers >= 1)
        uniqueIsos.push(student1DropoffIso);
      if (student2PickupIso && numberOfDrivers >= 2)
        uniqueIsos.push(student2PickupIso);
      if (student2DropoffIso && numberOfDrivers >= 2)
        uniqueIsos.push(student2DropoffIso);
      if (student3PickupIso && numberOfDrivers >= 3)
        uniqueIsos.push(student3PickupIso);
      if (student3DropoffIso && numberOfDrivers >= 3)
        uniqueIsos.push(student3DropoffIso);
      if (student4PickupIso && numberOfDrivers >= 4)
        uniqueIsos.push(student4PickupIso);
      if (student4DropoffIso && numberOfDrivers >= 4)
        uniqueIsos.push(student4DropoffIso);
      uniqueIsos = [...new Set(uniqueIsos)].sort((a, b) =>
        luxon.DateTime.fromISO(a) < luxon.DateTime.fromISO(b) ? -1 : 1
      );

      let childLessonTemplates = [];
      for (let i = 0; i < uniqueIsos.length - 1; i++) {
        const start = uniqueIsos[i];
        const end = uniqueIsos[i + 1];
        const duration = luxon.DateTime.fromISO(end).diff(
          luxon.DateTime.fromISO(start),
          "minutes"
        ).minutes;

        const assignment = convertedAssignments.find(
          (assignment) =>
            luxon.DateTime.fromISO(start) >=
              luxon.DateTime.fromISO(assignment.start) &&
            luxon.DateTime.fromISO(end) <=
              luxon.DateTime.fromISO(assignment.end)
        );

        const isStudent1Present =
          numberOfDrivers >= 1 &&
          this.studentPickupIso1Target.value &&
          this.studentDropoffIso1Target.value &&
          luxon.DateTime.fromISO(start) >=
            luxon.DateTime.fromISO(this.studentPickupIso1Target.value) &&
          luxon.DateTime.fromISO(end) <=
            luxon.DateTime.fromISO(this.studentDropoffIso1Target.value);
        const isStudent2Present =
          numberOfDrivers >= 2 &&
          this.studentPickupIso2Target.value &&
          this.studentDropoffIso2Target.value &&
          luxon.DateTime.fromISO(start) >=
            luxon.DateTime.fromISO(this.studentPickupIso2Target.value) &&
          luxon.DateTime.fromISO(end) <=
            luxon.DateTime.fromISO(this.studentDropoffIso2Target.value);
        const isStudent3Present =
          numberOfDrivers >= 3 &&
          this.studentPickupIso3Target.value &&
          this.studentDropoffIso3Target.value &&
          luxon.DateTime.fromISO(start) >=
            luxon.DateTime.fromISO(this.studentPickupIso3Target.value) &&
          luxon.DateTime.fromISO(end) <=
            luxon.DateTime.fromISO(this.studentDropoffIso3Target.value);
        const isStudent4Present =
          numberOfDrivers >= 4 &&
          this.studentPickupIso4Target.value &&
          this.studentDropoffIso4Target.value &&
          luxon.DateTime.fromISO(start) >=
            luxon.DateTime.fromISO(this.studentPickupIso4Target.value) &&
          luxon.DateTime.fromISO(end) <=
            luxon.DateTime.fromISO(this.studentDropoffIso4Target.value);

        const observers = [];
        if (isStudent1Present && assignment.driver !== "1") {
          observers.push("1");
        }
        if (isStudent2Present && assignment.driver !== "2") {
          observers.push("2");
        }
        if (isStudent3Present && assignment.driver !== "3") {
          observers.push("3");
        }
        if (isStudent4Present && assignment.driver !== "4") {
          observers.push("4");
        }

        childLessonTemplates.push({
          start,
          end,
          duration,
          driver: assignment.driver,
          observers,
        });
      }

      let isoTimesToLocationIds = {};
      if (
        numberOfDrivers >= 4 &&
        this.studentPickupLocationId4Target.value &&
        `${this.studentPickupLocationId4Target.value}` !== "0"
      ) {
        if (
          this.studentPickupLocationId4Target.value &&
          `${this.studentPickupLocationId4Target.value}` !== "0" &&
          isoTimesToLocationIds[this.studentPickupIso4Target.value]
        ) {
          this.errors.push(
            `Cannot be in two places at once at ${luxon.DateTime.fromISO(
              this.studentPickupIso4Target.value,
              {
                zone: this.data.get("drivingSchoolTimezone"),
              }
            ).toFormat("HH:mm")}`
          );
          return;
        }

        isoTimesToLocationIds[this.studentPickupIso4Target.value] =
          this.studentPickupLocationId4Target.value;
      }
      if (
        numberOfDrivers >= 4 &&
        this.studentDropoffLocationId4Target.value &&
        `${this.studentDropoffLocationId4Target.value}` !== "0"
      ) {
        if (
          this.studentDropoffLocationId4Target.value &&
          `${this.studentDropoffLocationId4Target.value}` !== "0" &&
          isoTimesToLocationIds[this.studentDropoffIso4Target.value]
        ) {
          console.log(
            "Conflict at ",
            this.studentDropoffIso4Target.value,
            isoTimesToLocationIds[this.studentDropoffIso4Target.value]
          );
          this.errors.push(
            `Cannot be in two places at once at ${luxon.DateTime.fromISO(
              this.studentDropoffIso4Target.value,
              {
                zone: this.data.get("drivingSchoolTimezone"),
              }
            ).toFormat("HH:mm")}`
          );
          return;
        }

        isoTimesToLocationIds[this.studentDropoffIso4Target.value] =
          this.studentDropoffLocationId4Target.value;
      }
      if (
        numberOfDrivers >= 3 &&
        this.studentPickupLocationId3Target.value &&
        `${this.studentPickupLocationId3Target.value}` !== "0"
      ) {
        if (
          this.studentPickupLocationId3Target.value &&
          `${this.studentPickupLocationId3Target.value}` !== "0" &&
          isoTimesToLocationIds[this.studentPickupIso3Target.value]
        ) {
          this.errors.push(
            `Cannot be in two places at once at ${luxon.DateTime.fromISO(
              this.studentPickupIso3Target.value,
              {
                zone: this.data.get("drivingSchoolTimezone"),
              }
            ).toFormat("HH:mm")}`
          );
          return;
        }

        isoTimesToLocationIds[this.studentPickupIso3Target.value] =
          this.studentPickupLocationId3Target.value;
      }
      if (
        numberOfDrivers >= 3 &&
        this.studentDropoffLocationId3Target.value &&
        `${this.studentDropoffLocationId3Target.value}` !== "0"
      ) {
        if (
          this.studentDropoffLocationId3Target.value &&
          `${this.studentDropoffLocationId3Target.value}` !== "0" &&
          isoTimesToLocationIds[this.studentDropoffIso3Target.value]
        ) {
          console.log(
            "Conflict at ",
            this.studentDropoffIso3Target.value,
            isoTimesToLocationIds[this.studentDropoffIso3Target.value]
          );
          this.errors.push(
            `Cannot be in two places at once at ${luxon.DateTime.fromISO(
              this.studentDropoffIso3Target.value,
              {
                zone: this.data.get("drivingSchoolTimezone"),
              }
            ).toFormat("HH:mm")}`
          );
          return;
        }

        isoTimesToLocationIds[this.studentDropoffIso3Target.value] =
          this.studentDropoffLocationId3Target.value;
      }
      if (
        numberOfDrivers >= 2 &&
        this.studentPickupLocationId2Target.value &&
        `${this.studentPickupLocationId2Target.value}` !== "0"
      ) {
        if (
          this.studentPickupLocationId2Target.value &&
          `${this.studentPickupLocationId2Target.value}` !== "0" &&
          isoTimesToLocationIds[this.studentPickupIso2Target.value]
        ) {
          console.log(
            "Conflict at ",
            this.studentPickupIso2Target.value,
            isoTimesToLocationIds[this.studentPickupIso2Target.value]
          );
          this.errors.push(
            `Cannot be in two places at once at ${luxon.DateTime.fromISO(
              this.studentPickupIso2Target.value,
              {
                zone: this.data.get("drivingSchoolTimezone"),
              }
            ).toFormat("HH:mm")}`
          );
          return;
        }

        isoTimesToLocationIds[this.studentPickupIso2Target.value] =
          this.studentPickupLocationId2Target.value;
      }
      if (
        numberOfDrivers >= 2 &&
        this.studentDropoffLocationId2Target.value &&
        `${this.studentDropoffLocationId2Target.value}` !== "0"
      ) {
        if (
          this.studentDropoffLocationId2Target.value &&
          `${this.studentDropoffLocationId2Target.value}` !== "0" &&
          isoTimesToLocationIds[this.studentDropoffIso2Target.value]
        ) {
          console.log(
            "Conflict at ",
            this.studentDropoffIso2Target.value,
            isoTimesToLocationIds[this.studentDropoffIso2Target.value]
          );
          this.errors.push(
            `Cannot be in two places at once at ${luxon.DateTime.fromISO(
              this.studentDropoffIso2Target.value,
              {
                zone: this.data.get("drivingSchoolTimezone"),
              }
            ).toFormat("HH:mm")}`
          );
          return;
        }

        isoTimesToLocationIds[this.studentDropoffIso2Target.value] =
          this.studentDropoffLocationId2Target.value;
      }
      if (
        numberOfDrivers >= 1 &&
        this.studentPickupLocationId1Target.value &&
        `${this.studentPickupLocationId1Target.value}` !== "0"
      ) {
        if (
          this.studentPickupLocationId1Target.value &&
          `${this.studentPickupLocationId1Target.value}` !== "0" &&
          isoTimesToLocationIds[this.studentPickupIso1Target.value]
        ) {
          console.log(
            "Conflict at ",
            this.studentPickupIso1Target.value,
            isoTimesToLocationIds[this.studentPickupIso1Target.value]
          );
          this.errors.push(
            `Cannot be in two places at once at ${luxon.DateTime.fromISO(
              this.studentPickupIso1Target.value,
              {
                zone: this.data.get("drivingSchoolTimezone"),
              }
            ).toFormat("HH:mm")}`
          );
          return;
        }

        isoTimesToLocationIds[this.studentPickupIso1Target.value] =
          this.studentPickupLocationId1Target.value;
      }
      if (
        numberOfDrivers >= 1 &&
        this.studentDropoffLocationId1Target.value &&
        `${this.studentDropoffLocationId1Target.value}` !== "0"
      ) {
        if (
          this.studentDropoffLocationId1Target.value &&
          `${this.studentDropoffLocationId1Target.value}` !== "0" &&
          isoTimesToLocationIds[this.studentDropoffIso1Target.value]
        ) {
          console.log(
            "Conflict at ",
            this.studentDropoffIso1Target.value,
            isoTimesToLocationIds[this.studentDropoffIso1Target.value]
          );
          this.errors.push(
            `Cannot be in two places at once at ${luxon.DateTime.fromISO(
              this.studentDropoffIso1Target.value,
              {
                zone: this.data.get("drivingSchoolTimezone"),
              }
            ).toFormat("HH:mm")}`
          );
          return;
        }

        isoTimesToLocationIds[this.studentDropoffIso1Target.value] =
          this.studentDropoffLocationId1Target.value;
      }
      if (
        this.masterLessonPickupLocationIdInputTarget.value &&
        `${this.masterLessonPickupLocationIdInputTarget.value}` !== "0"
      ) {
        if (
          this.masterLessonPickupLocationIdInputTarget.value &&
          `${this.masterLessonPickupLocationIdInputTarget.value}` !== "0" &&
          isoTimesToLocationIds[masterPickupIso]
        ) {
          console.log(
            "Conflict at ",
            masterPickupIso,
            isoTimesToLocationIds[masterPickupIso]
          );
          this.errors.push(
            `Cannot be in two places at once at ${luxon.DateTime.fromISO(
              masterPickupIso,
              {
                zone: this.data.get("drivingSchoolTimezone"),
              }
            ).toFormat("HH:mm")}`
          );
          return;
        }

        isoTimesToLocationIds[
          masterPickupIso
        ] = `${this.masterLessonPickupLocationIdInputTarget.value}`;
      } else {
        this.errors.push("Lesson pickup location is not set");
        return;
      }
      if (
        this.masterLessonDropoffLocationIdInputTarget.value &&
        `${this.masterLessonDropoffLocationIdInputTarget.value}` !== "0"
      ) {
        if (
          this.masterLessonDropoffLocationIdInputTarget.value &&
          `${this.masterLessonDropoffLocationIdInputTarget.value}` !== "0" &&
          isoTimesToLocationIds[masterDropoffIso]
        ) {
          this.errors.push(
            `Cannot be in two places at once at ${luxon.DateTime.fromISO(
              masterDropoffIso,
              {
                zone: this.data.get("drivingSchoolTimezone"),
              }
            ).toFormat("HH:mm")}`
          );
          return;
        }

        isoTimesToLocationIds[
          masterDropoffIso
        ] = `${this.masterLessonDropoffLocationIdInputTarget.value}`;
      } else {
        this.errors.push("Lesson dropoff location is not set");
        return;
      }

      childLessonTemplates = childLessonTemplates.map((template) => {
        return {
          ...template,
          pickupLocationId: isoTimesToLocationIds[template.start] || null,
          dropoffLocationId: isoTimesToLocationIds[template.end] || null,
        };
      });

      this.processedJson = JSON.stringify(childLessonTemplates, null, 2);
    }
  }

  hideOrShowOutputDebug() {
    this.processedJsonTarget.value = this.processedJson;

    if (this.errors.length > 0) {
      this.errorOutputTarget.classList.remove("u-hidden");
      this.errorMessagesTarget.innerHTML = "";
      for (const error of this.errors) {
        const p = document.createElement("p");
        p.classList.add("mt1");
        p.classList.add("mb0");
        p.textContent = error;
        this.errorMessagesTarget.appendChild(p);
      }
    } else {
      this.errorOutputTarget.classList.add("u-hidden");
    }

    this.refreshSubmitButtonState();
  }

  /**
   * Hides and shows the student pickup and dropoff location selectors based on whether
   * the pickup/dropoff time matches the master lesson time.
   */
  hideAndShowStudentLocationInputs() {
    const masterPickupTime = this.getPickupTimeFromForm();
    const masterDropoffTime = this.getDropoffTimeFromForm();

    const student1PickupTime = this.getTimeFromTimeElement(
      this.studentPickupTimeInput1Target
    );
    if (masterPickupTime === student1PickupTime) {
      this.studentPickupLocationSelector1Target.classList.add("u-hidden");
    } else {
      this.studentPickupLocationSelector1Target.classList.remove("u-hidden");
    }
    const student1DropoffTime = this.getTimeFromTimeElement(
      this.studentDropoffTimeInput1Target
    );
    if (masterDropoffTime === student1DropoffTime) {
      this.studentDropoffLocationSelector1Target.classList.add("u-hidden");
    } else {
      this.studentDropoffLocationSelector1Target.classList.remove("u-hidden");
    }

    const student2PickupTime = this.getTimeFromTimeElement(
      this.studentPickupTimeInput2Target
    );
    if (masterPickupTime === student2PickupTime) {
      this.studentPickupLocationSelector2Target.classList.add("u-hidden");
    } else {
      this.studentPickupLocationSelector2Target.classList.remove("u-hidden");
    }
    const student2DropoffTime = this.getTimeFromTimeElement(
      this.studentDropoffTimeInput2Target
    );
    if (masterDropoffTime === student2DropoffTime) {
      this.studentDropoffLocationSelector2Target.classList.add("u-hidden");
    } else {
      this.studentDropoffLocationSelector2Target.classList.remove("u-hidden");
    }

    const student3PickupTime = this.getTimeFromTimeElement(
      this.studentPickupTimeInput3Target
    );
    if (masterPickupTime === student3PickupTime) {
      this.studentPickupLocationSelector3Target.classList.add("u-hidden");
    } else {
      this.studentPickupLocationSelector3Target.classList.remove("u-hidden");
    }
    const student3DropoffTime = this.getTimeFromTimeElement(
      this.studentDropoffTimeInput3Target
    );
    if (masterDropoffTime === student3DropoffTime) {
      this.studentDropoffLocationSelector3Target.classList.add("u-hidden");
    } else {
      this.studentDropoffLocationSelector3Target.classList.remove("u-hidden");
    }

    const student4PickupTime = this.getTimeFromTimeElement(
      this.studentPickupTimeInput4Target
    );
    if (masterPickupTime === student4PickupTime) {
      this.studentPickupLocationSelector4Target.classList.add("u-hidden");
    } else {
      this.studentPickupLocationSelector4Target.classList.remove("u-hidden");
    }
    const student4DropoffTime = this.getTimeFromTimeElement(
      this.studentDropoffTimeInput4Target
    );
    if (masterDropoffTime === student4DropoffTime) {
      this.studentDropoffLocationSelector4Target.classList.add("u-hidden");
    } else {
      this.studentDropoffLocationSelector4Target.classList.remove("u-hidden");
    }
  }

  /**
   * Show the correct number of student tabs based on the dropdown selection
   */
  showAppropriateAmountOfStudentTabs() {
    const count = parseInt(this.masterLessonDriverCountSelectTarget.value, 10);

    if (count >= 1) {
      this.tab1Target.classList.remove("u-hidden");
    } else {
      this.tab1Target.classList.add("u-hidden");
    }

    if (count >= 2) {
      this.tab2Target.classList.remove("u-hidden");
    } else {
      this.tab2Target.classList.add("u-hidden");
      if (this.tab2Target.classList.contains("is-active")) {
        setTimeout(() => {
          this.tab1Target.click();
        }, 0);
      }
    }

    if (count >= 3) {
      this.tab3Target.classList.remove("u-hidden");
    } else {
      this.tab3Target.classList.add("u-hidden");
      if (this.tab3Target.classList.contains("is-active")) {
        setTimeout(() => {
          this.tab1Target.click();
        }, 0);
      }
    }

    if (count >= 4) {
      this.tab4Target.classList.remove("u-hidden");
    } else {
      this.tab4Target.classList.add("u-hidden");
      if (this.tab4Target.classList.contains("is-active")) {
        setTimeout(() => {
          this.tab1Target.click();
        }, 0);
      }
    }
  }

  /**
   * Split the time range into segments of the desired length.
   * @param {*} startIso
   * @param {*} endIso
   * @param {*} desiredLengthMinutes
   * @returns
   */
  splitIsosUsingDesiredLength(startIso, endIso, desiredLengthMinutes) {
    const start = luxon.DateTime.fromISO(startIso, {
      zone: this.data.get("drivingSchoolTimezone"),
    });
    const end = luxon.DateTime.fromISO(endIso, {
      zone: this.data.get("drivingSchoolTimezone"),
    });

    let output = [];
    let time = start;
    while (time < end) {
      let endTime = time.plus({ minutes: desiredLengthMinutes });
      if (endTime > end) {
        endTime = end;
      }
      output.push({
        start: time.toISO(),
        end: endTime.toISO(),
        durationMinutes: time.diff(endTime, "minutes").minutes,
      });
      time = endTime;
    }
    return output;
  }

  /**
   * Takes a start and end iso datetime and splits it into the given number of segments.
   * For example starting at 10:00 and ending at 11:00 with 2 segments would return:
   * [{start: 10:00, end: 10:30}, {start: 10:30, end: 11:00}]
   * If the time cannot be split evenly the last segment will be slightly longer.
   * @param {*} startIso
   * @param {*} endIso
   * @param {*} segments
   * @returns
   */
  splitIsos(startIso, endIso, segments) {
    const start = luxon.DateTime.fromISO(startIso, {
      zone: this.data.get("drivingSchoolTimezone"),
    });
    const end = luxon.DateTime.fromISO(endIso, {
      zone: this.data.get("drivingSchoolTimezone"),
    });

    const durationInMinutes = end.diff(start, "minutes").minutes;
    const segmentDuration = Math.min(durationInMinutes / segments);

    let output = [];
    let time = start;
    for (let i = 0; i < segments; i++) {
      let endTime = time.plus({ minutes: segmentDuration });
      output.push({
        start: time.toISO(),
        end: endTime.toISO(),
        durationMinutes: time.diff(endTime, "minutes").minutes,
      });
      time = endTime;
    }

    output[output.length - 1].end = end.toISO();
    return output;
  }

  isIsoValid(iso) {
    if (!iso) return false;
    const dt = luxon.DateTime.fromISO(iso);
    if (!dt.isValid) return false;
    // Should minutes be multiple of 5?
    if (dt.minute % 5 !== 0) return false;
    return true;
  }

  isTimeValid(time) {
    if (!time) return false;
    const [hours, minutes] = time.split(":").map((str) => parseInt(str, 10));
    if (isNaN(hours) || isNaN(minutes)) return false;
    if (hours < 0 || hours > 23) return false;
    if (minutes < 0 || minutes > 59) return false;
    // Minutes should be a multiple of 5
    if (minutes % 5 !== 0) return false;
    return true;
  }

  /**
   * Get the master lesson pickup time from the form.
   * @returns in format "HH:MM" or null if not set
   */
  getPickupTimeFromForm(debug = false) {
    return this.getTimeFromTimeElement(
      this.masterLessonPickupTimeInputTarget,
      debug
    );
  }

  /**
   * Get the master lesson pickup ISO datetime from the form.
   * @returns in ISO format or null if not set
   */
  getPickupTimeIsoFromForm() {
    return this.masterLessonPickupIsoInputTarget.value;
  }

  /**
   * Get the master lesson dropoff time based on the pickup time and the duration.
   * @returns
   */
  getDropoffTimeFromForm() {
    const pickupTime = this.getPickupTimeFromForm();
    const duration = this.masterLessonDurationSelectTarget.value;
    const dropoffTime = this.calculateDropoffTime(pickupTime, duration);
    return dropoffTime;
  }

  /**
   * Get the master lesson dropoff ISO datetime based on the pickup ISO datetime and the duration.
   * @returns
   */
  getDropoffIsoFromForm() {
    try {
      const dropoffTime = this.getDropoffTimeFromForm();
      if (!dropoffTime) return null;

      const pickupIso = this.masterLessonPickupIsoInputTarget.value;
      if (!pickupIso) return null;

      const timezone = this.data.get("drivingSchoolTimezone");
      if (!timezone) return null;

      const dateTime = luxon.DateTime.fromISO(pickupIso, { zone: timezone });
      if (!dateTime.isValid) return null;

      const [hours, minutes] = dropoffTime
        .split(":")
        .map((str) => parseInt(str, 10));
      if (isNaN(hours) || isNaN(minutes)) return null;

      const dropoffDateTime = dateTime.set({ hour: hours, minute: minutes });
      if (!dropoffDateTime.isValid) return null;

      return dropoffDateTime.toISO();
    } catch (error) {
      return null;
    }
  }

  /**
   * Gets the time value from a time input element.
   * @param {*} timeElement
   * @returns
   */
  getTimeFromTimeElement(timeElement, debug = false) {
    if (!timeElement) {
      if (debug) console.log("No time element provided");
      return null;
    }
    const timeValue = timeElement.value;
    if (!timeValue) {
      if (debug) console.log("No time value");
      return null;
    }
    const trimmedValue = timeValue.trim();
    if (trimmedValue === "") {
      if (debug) console.log("Time value is empty after trimming");
      return null;
    }
    return trimmedValue;
  }

  /**
   * Calculates a time to drop a student off based on their pickup time and the lesson duration.
   * @param {*} pickupTime in format "HH:MM"
   * @param {*} durationInMinutes number of minutes
   * @returns in format "HH:MM" or null if invalid input
   */
  calculateDropoffTime(pickupTime, durationInMinutes) {
    try {
      if (!pickupTime) return null;
      if (!durationInMinutes) return null;

      const [hours, minutes] = pickupTime
        .split(":")
        .map((str) => parseInt(str, 10));
      if (isNaN(hours) || isNaN(minutes)) return null;

      const durationNumber = parseInt(durationInMinutes, 10);
      if (isNaN(durationNumber)) return null;

      let totalMinutes = hours * 60 + minutes + durationNumber;
      totalMinutes = totalMinutes % (24 * 60); // wrap around after 24 hours

      const dropoffHours = Math.floor(totalMinutes / 60);
      const dropoffMinutes = totalMinutes % 60;

      const formattedHours = String(dropoffHours).padStart(2, "0");
      const formattedMinutes = String(dropoffMinutes).padStart(2, "0");

      return `${formattedHours}:${formattedMinutes}`;
    } catch (error) {
      console.error("Error calculating dropoff time:", error);
      return null;
    }
  }

  highestCommonDenominator(numbers) {
    const gcd = (a, b) => {
      if (!b) return a;
      return gcd(b, a % b);
    };

    return numbers.reduce((a, b) => gcd(a, b));
  }

  csrfToken() {
    const el = document.querySelector("meta[name='csrf-token']");
    return el && el.content;
  }

  isSubmitBlockedByPackagePreview() {
    return this.packagePreviewIsLoading || this.packagePreviewHasBlockingIssues;
  }

  refreshSubmitButtonState() {
    this.submitButtonTarget.disabled =
      this.isSubmitting ||
      this.errors.length > 0 ||
      this.isSubmitBlockedByPackagePreview();
  }

  setLoading(isLoading) {
    this.isSubmitting = isLoading;
    if (isLoading) {
      let button = this.submitButtonTarget;
      let spinner = button.querySelector(".Spinner");
      spinner.classList.remove("u-hidden");
      spinner.classList.add("u-inlineBlock");
    } else {
      let button = this.submitButtonTarget;
      let spinner = button.querySelector(".Spinner");
      spinner.classList.add("u-hidden");
      spinner.classList.remove("u-inlineBlock");
    }

    this.refreshSubmitButtonState();
  }
}

/////// BELOW HERE ARE GENERIC FUNCTIONS NOT PART OF THE CONTROLLER ///////
/////// Helper methods ////////

function formatMinutesLabel(minutes) {
  const parsedMinutes = parseInt(minutes, 10);
  if (isNaN(parsedMinutes)) return "n/a";
  if (parsedMinutes === 0) return "0m";

  const sign = parsedMinutes < 0 ? "-" : "";
  const absoluteMinutes = Math.abs(parsedMinutes);
  const hours = Math.floor(absoluteMinutes / 60);
  const remainingMinutes = absoluteMinutes % 60;
  const parts = [];

  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (remainingMinutes > 0) {
    parts.push(`${remainingMinutes}m`);
  }

  if (parts.length === 0) {
    parts.push("0m");
  }

  return `${sign}${parts.join(" ")}`;
}

function escapeHtml(value) {
  return `${value ?? ""}`
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function capitalizeLabel(value) {
  if (!value) return "";
  return `${value}`.charAt(0).toUpperCase() + `${value}`.slice(1);
}

function packagePreviewStatusMeta(previewStatus) {
  if (previewStatus === "btw_override") {
    return {
      accentColor: "#9a6b00",
      backgroundColor: "#fff8e6",
    };
  }

  if (previewStatus === "blocked") {
    return {
      accentColor: "#b04545",
      backgroundColor: "#fff7f7",
    };
  }

  if (
    previewStatus === "admin_acknowledged_partial" ||
    previewStatus === "admin_acknowledged_no_package"
  ) {
    return {
      accentColor: "#9a6b00",
      backgroundColor: "#fff8e6",
    };
  }

  return {
    accentColor: "#2f7d32",
    backgroundColor: "#f7fbf7",
  };
}

function previewStudentLabel(attempt) {
  return attempt?.pupil_name || "Student";
}

function previewPackageLabel(packageName) {
  return packageName || "Package";
}

function buildPackagePreviewShortfallLabel(attempt) {
  const requiresAdminAcknowledgement = Boolean(
    attempt?.requires_admin_package_time_acknowledgement
  );
  const coveredDriveTime = requiresAdminAcknowledgement
    ? attempt?.acknowledged_drive_time_to_use
    : attempt?.will_use_drive_time;
  const coveredObservationTime = requiresAdminAcknowledgement
    ? attempt?.acknowledged_observation_time_to_use
    : attempt?.will_use_observation_time;

  const driveShortfall = Math.max(
    parseInt(attempt?.calculated_drive_time, 10) - parseInt(coveredDriveTime || 0, 10),
    0
  );
  const observationShortfall = Math.max(
    parseInt(attempt?.calculated_observation_time, 10) -
      parseInt(coveredObservationTime || 0, 10),
    0
  );
  const shortfallParts = [];

  if (driveShortfall > 0) {
    shortfallParts.push(`${formatMinutesLabel(driveShortfall)} drive`);
  }

  if (observationShortfall > 0) {
    shortfallParts.push(
      `${formatMinutesLabel(observationShortfall)} observation`
    );
  }

  if (shortfallParts.length === 0) {
    shortfallParts.push(
      `${formatMinutesLabel(parseInt(attempt?.calculated_drive_time, 10) || 0)} drive`
    );

    if ((parseInt(attempt?.calculated_observation_time, 10) || 0) > 0) {
      shortfallParts.push(
        `${formatMinutesLabel(
          parseInt(attempt?.calculated_observation_time, 10) || 0
        )} observation`
      );
    }
  }

  return `needs ${shortfallParts.join(" / ")}`;
}

function sanitizePreviewText(value) {
  return `${value ?? ""}`
    .replace(/\bBooking #\d+\b/g, "Booking")
    .replace(/\bPayment link #\d+\b/g, "Payment link")
    .replace(/\bPurchase #\d+\b/g, "Package")
    .replace(/\bPupil \d+\b/g, "Student")
    .replace(/\bpupil \d+\b/g, "student");
}

function divideBy5Minutes(minutes) {
  return Math.floor(minutes / 5);
}

function convert5MinuteIntegerToIsoTime(
  baseIsoTime,
  fiveMinuteInteger,
  timezone
) {
  const baseTime = luxon.DateTime.fromISO(baseIsoTime, { zone: timezone });

  const totalMinutes = fiveMinuteInteger * 5;
  const hoursToAdd = Math.floor(totalMinutes / 60);
  const minutesToAdd = totalMinutes % 60;

  const newTime = baseTime.plus({ hours: hoursToAdd, minutes: minutesToAdd });

  return newTime.toISO();
}

function get5MinuteIntegerFromIsoTime(baseIsoTime, isoTime, timezone) {
  const baseTime = luxon.DateTime.fromISO(baseIsoTime, { zone: timezone });
  const time = luxon.DateTime.fromISO(isoTime, { zone: timezone });

  const baseTimeInMinutes = baseTime.hour * 60 + baseTime.minute;
  const timeInMinutes = time.hour * 60 + time.minute;

  const output = divideBy5Minutes(timeInMinutes - baseTimeInMinutes);
  return output;
}

/**
 * Tries to split the lesson time between the drivers.
 * Returns an with success true/false and either the assignment or errors.
 * The assignments are start/end with duration and driver name.
 * @param {*} totalLessonTime
 * @param {*} drivers array with items like this { name: "Alice", start: 0, end: 24, drivingTime: 12 }
 * @returns Item with success true/false and assignment array with items like this { start: 0, end: 60, duration: 60, driver: "Alice" } or errors array
 */
function splitLessonTimeAmongDrivers(totalLessonTime, drivers) {
  const totalDriveTime = drivers.reduce(
    (sum, driver) => sum + driver.drivingTime,
    0
  );

  if (totalDriveTime !== totalLessonTime) {
    return {
      success: false,
      errors: [
        `Lesson duration and students total driving duration do not match.`,
      ],
    };
  }

  // Build assignments incrementally with backtracking.
  // Try fewest switches first (1 segment, then 2, etc.)
  for (let numSegments = 1; numSegments <= 5; numSegments++) {
    const driverTimeNeeded = {};
    for (const driver of drivers) {
      driverTimeNeeded[driver.name] = driver.drivingTime;
    }

    const result = buildAssignment(0, totalLessonTime, drivers, driverTimeNeeded, numSegments, null);
    if (result) {
      return { success: true, assignment: result };
    }
  }

  return {
    success: false,
    errors: ["No valid driver assignment found."],
  };
}

function buildAssignment(position, totalLessonTime, drivers, driverTimeNeeded, segmentsLeft, lastDriver) {
  if (position === totalLessonTime) {
    for (const name in driverTimeNeeded) {
      if (driverTimeNeeded[name] !== 0) return null;
    }
    return [];
  }

  if (segmentsLeft === 0) return null;

  for (const driver of drivers) {
    if (driver.name === lastDriver) continue;
    if (driverTimeNeeded[driver.name] <= 0) continue;
    if (position < driver.start) continue;

    const maxDuration = Math.min(
      driverTimeNeeded[driver.name],
      driver.end - position,
      totalLessonTime - position
    );

    for (let duration = 1; duration <= maxDuration; duration++) {
      driverTimeNeeded[driver.name] -= duration;

      const rest = buildAssignment(
        position + duration, totalLessonTime, drivers,
        driverTimeNeeded, segmentsLeft - 1, driver.name
      );

      if (rest) {
        return [{ start: position, end: position + duration, duration, driver: driver.name }, ...rest];
      }

      driverTimeNeeded[driver.name] += duration;
    }
  }

  return null;
}

/**
 * Generates all combinations of a specific length from a range of numbers.
 * For example, (3, 1, 5) would give:
 * [1, 2, 3]
 * [1, 2, 4]
 * [1, 2, 5]
 * [1, 3, 4]
 * ...
 * [3, 4, 5]
 * @param {*} arrayLength
 * @param {*} minValue
 * @param {*} maxValue
 * @returns
 */
function incrementalArrays(arrayLength, minValue, maxValue) {
  const results = [];

  function backtrack(start, path) {
    if (path.length === arrayLength) {
      results.push([...path]);
      return;
    }

    for (let i = start; i <= maxValue; i++) {
      path.push(i);
      backtrack(i + 1, path);
      path.pop();
    }
  }

  backtrack(minValue, []);
  return results;
}

/**
 * Returns all permutations of a given length using the possible values.
 * @param {*} arrayLength
 * @param {*} possibileValues
 * @returns
 */
function allPermutations(arrayLength, possibileValues) {
  const results = [];
  const max = possibileValues.length ** arrayLength;

  for (let i = 0; i < max; i++) {
    const permutation = [];
    let value = i;
    for (let j = 0; j < arrayLength; j++) {
      permutation.push(possibileValues[value % possibileValues.length]);
      value = Math.floor(value / possibileValues.length);
    }
    results.push(permutation);
  }

  return results;
}
